import React, { useState, useRef, useEffect, useContext } from "react";
import { DeSoIdentityContext } from "react-deso-protocol";
import { deso_graphql } from "../utils/graphql";
import { createUserAssociation, deleteUserAssociation, getUserAssociations, identity, uploadImage } from "deso-protocol";
import { localFees, systemKey } from "..";
import { Alert, Button, Card, Col, Container, Dropdown, Form, InputGroup, Modal, Row, Spinner } from "react-bootstrap";
import { handleLogin, UserDropdown } from "../appLoader";
import { localCurrency } from "../utils/currency";
import { useUserPreferences } from "../contexts/UserPreferences";
import ExchangeRatesContext from "../contexts/ExchangeRatesContext";
import { AccessGroupsContext } from "../contexts/AccessGroups";
import { encryptString } from "./StorageHelpers";
import { Upload } from "./Upload";

const MAX_ASSOCIATION_SIZE = 25000;
const BUFFER_SIZE = 200;
const associationType = "IMAGE_TEST";
const MAX_UPLOAD_WIDTH = 1500;
const sizeMap = {
    full: null,
    large: 5000,
    medium: 2500,
    default: 1500,
    small: 1000,
    tiny: 500,
};
const LOCAL_FEE = 0.0001;
const ASSOCIATION_COST = 0.000149758 + LOCAL_FEE;

export const Storage = () => {
    const { currentUser, isLoading } = useContext(DeSoIdentityContext);
    const { preferences } = useUserPreferences();
    const { accessGroups } = useContext(AccessGroupsContext);
    const { exchangeRates } = useContext(ExchangeRatesContext);

    const fileInputRef = useRef();

    const [fetchedImages, setFetchedImages] = useState([]);
    const [retrievedFile, setRetrievedFile] = useState(null);
    const [retrievedFileDetails, setRetrievedFileDetails] = useState(null);
    const [processingTime, setProcessingTime] = useState(null);

    const [refresh, setRefresh] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const [uploadModal, setUploadModal] = useState(false);

    const [endCursor, setEndCursor] = useState(null);
    const [moreResults, setMoreResults] = useState(false);
    const [mode, setMode] = useState("user");
    const [view, setView] = useState("list");

    const triggerFileInput = () => fileInputRef.current?.click();

    useEffect(() => {
        if (currentUser) {
            fetchAssociations();
        }
    }, [currentUser]);

    useEffect(() => {
        if (refresh) {
            setFetchedImages([]);
            fetchAssociations();
            setRefresh(false);
        }
    }, [refresh]);

    const fetchAssociations = async (fetchMore = false) => {
        const query = `
            query UserAssociations($first: Int, $condition: UserAssociationCondition, $orderBy: [UserAssociationsOrderBy!], $filter: UserAssociationFilter, $after: Cursor) {
                    userAssociations(first: $first, condition: $condition, orderBy: $orderBy, filter: $filter, after: $after) {
                        nodes {
                            associationType
                            associationValue
                            extraData
                            transactor {
                                username
                                publicKey
                            } 
                        }
                        totalCount
                        pageInfo {
                            hasNextPage
                            hasPreviousPage
                            startCursor
                            endCursor
                        }
                    }
            }
        `;

        const variables = {
            first: 12,
            condition: {
                associationType,
            },
            filter: {
                associationValue: {
                    endsWith: "_0",
                },
            },
            orderBy: "BLOCK_HEIGHT_DESC"
        };
        if (fetchMore && endCursor) {
            variables.after = endCursor;
        }
        if (mode === "user" && currentUser) {
            variables.condition.transactorPkid = currentUser.PublicKeyBase58Check;
        }

        const res = await deso_graphql({ operationName: "UserAssociations", variables, query });
        //console.log("Images found:", res);
        if (res?.data?.userAssociations?.nodes?.length) {
            if (fetchMore) {
                setFetchedImages((prev) => [...prev, ...res.data.userAssociations.nodes]);
            } else {
                setFetchedImages(res.data.userAssociations.nodes);
            }
        }

        if (res?.data?.userAssociations?.pageInfo?.hasNextPage) {
            setMoreResults(true);
            setEndCursor(res?.data?.userAssociations?.pageInfo?.endCursor)
        } else {
            setMoreResults(false);
            setEndCursor(null);
        }
    };

    const loadImageChunksAPI = async (item) => {
        if (!item) return;

        const totalStart = performance.now();
        const queryStart = performance.now();

        let allChunks = [];
        let lastSeenID = "";
        let hasMore = true;
        let filetype = "image/webp";
        let filename = '';
        //console.log("Loading chunks for", item);
        //let associationValuePrefix = item?.extraData?.ImageURL ? item?.extraData?.ImageURL + "_" : item?.extraData?.filename + "_";
        let associationValuePrefix = item?.associationValue?.split("_").slice(0, -1).join("_");
        //console.log("AssociationValuePrefix", associationValuePrefix);

        while (hasMore) {
            const associations = await getUserAssociations({
                AssociationType: associationType,
                AssociationValuePrefix: associationValuePrefix,
                TransactorPublicKeyBase58Check: item?.transactor.publicKey,
                Limit: 100,
                LastSeenAssociationID: lastSeenID || undefined,
            });

            const chunks = associations?.Associations || [];
            allChunks = [...allChunks, ...chunks];

            //console.log("Chunk[0]", chunks[0]);
            setRetrievedFileDetails(chunks[0]);
            filetype = chunks?.[0]?.ExtraData?.filetype || "image/webp";
            filename = chunks?.[0]?.ExtraData?.filename || "untitled";

            if (chunks.length < 100) {
                hasMore = false;
            } else {
                lastSeenID = chunks[chunks.length - 1].AssociationID;
            }
        }

        const queryEnd = performance.now();
        const recombineStart = performance.now();

        const ordered = allChunks.sort((a, b) => {
            const ai = parseInt(a.AssociationValue.split("_").pop());
            const bi = parseInt(b.AssociationValue.split("_").pop());
            return ai - bi;
        });

        const combined = ordered.map(c => c.ExtraData?.content || "").join("");
        const mimeType = filetype || "image/webp";  // Default to "image/webp" if file.type is not available
        let dataUri = combined;
        if (!dataUri.startsWith("data:")) {
            dataUri = `data:${mimeType};base64,${dataUri}`;
        }
        //console.log("Recombined content", dataUri);
        setRetrievedFile(dataUri);

        const recombineEnd = performance.now();
        const totalEnd = performance.now();
        setProcessingTime({
            totalTime: ((totalEnd - totalStart) / 1000).toFixed(2),
            queryTime: ((queryEnd - queryStart) / 1000).toFixed(2),
            recombinationTime: ((recombineEnd - recombineStart) / 1000).toFixed(2),
        });
    };


    const loadImageChunksQL = async (item) => {
        if (!item) return;

        const totalStart = performance.now();
        const queryStart = performance.now();
        const query = `
            query UserAssociations($first: Int, $condition: UserAssociationCondition, $filter: UserAssociationFilter, $after: Cursor) {
                userAssociations(first: $first, condition: $condition, filter: $filter, after: $after) {
                    nodes {
                        associationValue
                        extraData
                    }
                    totalCount
                    pageInfo {
                        hasNextPage
                        hasPreviousPage
                        startCursor
                        endCursor
                    }
                }
            }
        `;
        let allChunks = [];
        let hasNextPage = true;
        let afterCursor = null;
        let filetype = "image/webp";
        let filename = '';

        // Loop to get all pages of results
        //let associationValuePrefix = item?.extraData?.ImageURL ? item?.extraData?.ImageURL + "_" : item?.extraData?.filename + "_";
        let associationValuePrefix = item?.associationValue?.split("_").slice(0, -1).join("_");

        while (hasNextPage) {
            const variables = {
                first: 100,
                condition: {
                    associationType,
                },
                filter: {
                    associationValue: {
                        startsWith: associationValuePrefix,
                    },
                },
                after: afterCursor,
            };

            //console.log("Variables:", variables);

            const res = await deso_graphql({ operationName: "UserAssociations", variables, query });

            const chunks = res?.data?.userAssociations?.nodes || [];
            const pageInfo = res?.data?.userAssociations?.pageInfo || {};

            //console.log("Chunks", chunks);
            setRetrievedFileDetails(chunks[0]);
            filetype = chunks[0]?.extraData?.filetype || "image/webp";
            filename = chunks[0]?.extraData?.filename || "untitled";

            // Append current page's chunks to allChunks
            allChunks = [...allChunks, ...chunks];

            // Check if there's another page to fetch
            hasNextPage = pageInfo?.hasNextPage;
            afterCursor = pageInfo?.endCursor;
        }
        const queryEnd = performance.now();
        const recombineStart = performance.now();
        // Sort the chunks by the numeric part of the association value (i.e., the chunk index)
        const ordered = allChunks.sort((a, b) => {
            const ai = parseInt(a.associationValue.split("_").pop());
            const bi = parseInt(b.associationValue.split("_").pop());
            return ai - bi;
        });

        console.log("Ordered chunks", ordered);
        // Combine all the chunk contents into a single string
        const combined = ordered.map(c => c.extraData?.content || "").join("");
        const mimeType = filetype || "image/webp";  // Default to "image/webp" if file.type is not available
        let dataUri = combined;
        if (!dataUri.startsWith("data:")) {
            dataUri = `data:${mimeType};base64,${dataUri}`;
        }
        //console.log("Recombined content", dataUri);
        setRetrievedFile(dataUri);

        const recombineEnd = performance.now();
        const totalEnd = performance.now();
        setProcessingTime({
            totalTime: ((totalEnd - totalStart) / 1000).toFixed(2),
            queryTime: ((queryEnd - queryStart) / 1000).toFixed(2),
            recombinationTime: ((recombineEnd - recombineStart) / 1000).toFixed(2),
        });
    };

    const [deleteModal, setDeleteModal] = useState(false);
    const [imageToDelete, setImageToDelete] = useState(null);

    const handleDelete = async () => {
        const img = imageToDelete;
        setDeleting(true);

        try {

            // First get the matching associations
            const associations = await getUserAssociations({
                AssociationType: associationType,
                AssociationValuePrefix: img.extraData.ImageURL + "_",
                TransactorPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
            })

            //console.log("Associations to delete ("+associations?.Associations?.length+") - parts: ("+img?.extraData?.Parts+")",associations);

            for (let i = 0; i < associations?.Associations?.length; i++) {
                await deleteUserAssociation({
                    TransactorPublicKeyBase58Check: currentUser.PublicKeyBase58Check,
                    AssociationID: associations.Associations[i].AssociationID,
                    TransactionFees: [localFees()]
                });
                //console.log(`Deleted chunk `+i,associations?.Associations[i]);
            }

            // Optionally, update the state to reflect the changes

            //alert('Image and its chunks deleted successfully.');

        } catch (error) {
            console.error('Error deleting image:', error);
            alert('An error occurred while deleting the image.');
        }

        // Close the modal after deletion
        setDeleting(false);
        setDeleteModal(false);
        setRefresh(true);
    };

    const openImageInNewTab = () => {
        if (!retrievedFile?.startsWith("data:image/")) return;

        // Convert base64 to Blob
        const byteString = atob(retrievedFile.split(",")[1]);
        const mimeString = retrievedFile.split(",")[0].split(":")[1].split(";")[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([ab], { type: mimeString });
        const blobUrl = URL.createObjectURL(blob);

        // Open in new tab
        window.open(blobUrl, "_blank");
    };

    //console.log("Retrieved File", retrievedFileDetails, retrievedFile);
    return (
        <>
            <Container>
                <Row className="py-3">
                    <h1><i className="bi bi-link-45deg me-2"></i>On-chain Storage</h1>
                    <h2 className="text-muted">Proof of Concept</h2>
                    <p className="text-muted small">The standard API images (centralised at images.deso.org) are shown in the gallery for speed.</p>
                    <p className="text-muted small">Click API or QL to retrieve and recombine individual images that are stored fully on-chain.<br />The two options allow the speed of the API vs. GraphQL methods to be tested.</p>
                </Row>
            </Container>
            <Container>
                <Row>
                    <div className="d-flex flex-row align-items-center justify-content-end mb-3">
                        {currentUser ? (
                            <Button variant="primary" className='me-2' onClick={() => setUploadModal(true)} disabled={uploadModal}>
                                <i className="bi bi-upload me-2"></i>Upload
                            </Button>
                        ) : (
                            <Button variant="primary" className='me-2' onClick={handleLogin}>
                                Login to Upload
                            </Button>
                        )}
                        <Button onClick={() => { setRefresh(true); }}><i className="bi bi-arrow-clockwise me-2"></i>Refresh</Button>
                    </div>
                    <Button onClick={() => { encryptString("123456789012345678901234567890123456789012345678901234567890123456789012345678901234567890123456789012345678901234567890", 'New convo with DG', accessGroups); }}><i className="bi bi-arrow-clockwise me-2"></i>Encryption Test</Button>
                </Row>
            </Container>
            <Container>
                <Row className="mb-3">
                    <Col xs={12} className="d-flex flex-row flex-wrap justify-content-start align-items-center">
                        <h5 className="my-0 me-auto text-nowrap">
                            {mode === "user" ? "My Files" : "All Files"}
                        </h5>
                        <div className="d-flex flex-row align-items-center">
                            <span className="text-muted px-2">View</span>
                            <span className={`action mx-1 px-2 rounded ${view === "list" ? "text-info border-info border" : "text-muted"}`} onClick={() => setView("list")}>
                                <i className="bi bi-list"></i>
                            </span>
                            <span className={`action mx-1 px-2 rounded ${view === "grid" ? "text-info border-info border" : "text-muted"}`} onClick={() => setView("grid")}>
                                <i className="bi bi-grid-fill"></i>
                            </span>
                            <span className={`action mx-1 px-2 rounded ${view === "grid-large" ? "text-info border-info border" : "text-muted"}`} onClick={() => setView("grid-large")}>
                                <i className="bi bi-grid-3x3-gap-fill"></i>
                            </span>
                        </div>
                        <Dropdown className="ms-2" onSelect={(selectedKey) => {
                            setMode(selectedKey);
                            setRefresh(true);
                        }}>
                            <Dropdown.Toggle variant='outline-info'>
                                {mode === "user" ? <><i className="bi bi-person-fill me-2"></i> My Files</> : <><i className="bi bi-people-fill me-2"></i>All Files</>}
                            </Dropdown.Toggle>

                            <Dropdown.Menu className="py-0" align="end">
                                <Dropdown.Item eventKey="user" active={mode === "user"}>
                                    <i className="bi bi-person-fill me-2"></i> My Files
                                </Dropdown.Item>
                                <Dropdown.Item eventKey="all" active={mode === "all"}>
                                    <i className="bi bi-people-fill me-2"></i> All Files
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </Col>
                </Row>
                <Row className="g-3 pt-3 pb-5">
                    {fetchedImages.map((file, i) => {
                        // Extract the part before the last "_" in associationValue
                        const associationValuePrefix = file?.associationValue?.split("_").slice(0, -1).join("_");

                        return (
                            <Col
                                key={i}
                                xs={view === "list" ? 12 : view === "grid-large" ? 4 : 6}
                                sm={view === "list" ? 12 : view === "grid-large" ? 4 : 6}
                                md={view === "list" ? 12 : view === "grid-large" ? 3 : 4}
                                lg={view === "grid-large" ? 3 : view === "grid" ? 2 : 12}
                                className={`${view === "list" ? "my-1" : ""}`}
                            >
                                <Card className={`shadow hover ${view === "list" ? "p-0 border-0 rounded" : ""}`}>
                                    {view !== "list" && (
                                        <div className="ratio ratio-1x1 hover" onClick={() => loadImageChunksQL(file)}>
                                            {file?.extraData?.filetype?.startsWith("image/") || file?.extraData?.content?.startsWith('data:image') ? (
                                                <Card.Img
                                                    variant="top"
                                                    className="object-fit-cover"
                                                    src={`https://storage.w3ly.io/${associationValuePrefix}`} /*{file.extraData.ImageURL}*/
                                                />
                                            ) : (
                                                <div className="bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center w-100 h-100">
                                                    <i className={`bi ${getFileIcon(file?.extraData)}`} style={{ fontSize: "3rem" }}></i>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <Card.Body className={`${view === "list" ? "bg-body bg-opacity-10 d-flex flex-row align-items-center p-0 hover" : " pt-0 d-flex flex-column"}`}>
                                        {view === "list" && (
                                            <div className="d-flex align-items-center" onClick={() => loadImageChunksQL(file)}>
                                                {file?.extraData?.filetype?.startsWith("image/") || file?.extraData?.content?.startsWith('data:image') ? (
                                                    <img
                                                        src={`https://storage.w3ly.io/${associationValuePrefix}`} //{file.extraData.ImageURL}
                                                        alt={file?.extraData?.filename || "image"}
                                                        style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "4px" }}
                                                    />
                                                ) : (
                                                    <div className="bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: "50px", height: "50px", borderRadius: "4px" }}>
                                                        <i className={`bi ${getFileIcon(file?.extraData)}`} style={{ fontSize: "2rem" }}></i>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <div className={`${view === "list" ? "flex-fill text-start ps-4" : "w-100 py-2"} text-truncate d-block`} onClick={() => loadImageChunksQL(file)}>
                                            {file?.extraData?.filename || (view === "list" ? file.extraData?.ImageURL : file.extraData?.ImageURL?.slice(-14))}
                                        </div>
                                        <div className={`${view === "list" ? "text-start ps-4" : "w-100"} text-truncate d-block`}>
                                            {file?.extraData?.fileextension}
                                        </div>
                                        <div className={`${view === "list" ? "justify-content-end" : "w-100 justify-content-between"} d-flex flex-row align-items-center`}>
                                            {!associationValuePrefix.startsWith("https://") && (
                                                <>
                                                    {/*(file?.extraData?.filetype?.startsWith("image/") || file?.extraData?.content?.startsWith('data:image')) && (
                                                <>
                                                    <img src={`https://storage.w3ly.io/${associationValuePrefix}`} className="img-fluid h-100" />
                                                </>
                                            )*/}
                                                    <a className="btn btn-sm btn-info mx-2" href={`https://storage.w3ly.io/${associationValuePrefix}`} target="_blank" rel="noopener noreferrer">
                                                        <i className="bi bi-globe2 me-2"></i>URL
                                                    </a>
                                                </>
                                            )}
                                            <Button
                                                size="sm"
                                                variant="info"
                                                className="mx-2"
                                                onClick={() => loadImageChunksAPI(file)}
                                            >
                                                API
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="info"
                                                className="mx-2"
                                                onClick={() => loadImageChunksQL(file)}
                                            >
                                                QL
                                            </Button>
                                            {file.transactor.publicKey === currentUser?.PublicKeyBase58Check && (
                                                <Button variant="danger" size="sm" className="mx-2" onClick={() => {
                                                    setImageToDelete(file);
                                                    setDeleteModal(true); // Show the delete confirmation modal
                                                }}>
                                                    <i className="bi bi-trash3-fill"></i>
                                                </Button>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}

                    {moreResults && endCursor && (
                        <div className="w-100 text-center">
                            <Button onClick={() => fetchAssociations(true)}>
                                Load More
                            </Button>
                        </div>
                    )}
                </Row>
            </Container>

            <Modal
                show={!!retrievedFile}
                onHide={() => setRetrievedFile(null)}
                centered
                size="lg"
            >
                <Modal.Header className="border-0" closeButton>
                    <Modal.Title className="text-truncate fw-light"><i className={`bi ${getFileIcon(retrievedFileDetails?.extraData || retrievedFileDetails?.ExtraData)} me-2`}></i><strong>{retrievedFileDetails?.extraData?.filename || retrievedFileDetails?.ExtraData?.filename || retrievedFileDetails?.extraData?.ImageURL || "untitled"}</strong></Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-0">
                    <div className={`alert alert-secondary text-center mt-3 mb-0 small`}>
                        <strong>Total time:</strong> {processingTime?.totalTime}s<br />
                        <strong>Query time:</strong> {processingTime?.queryTime}s<br />
                        <strong>Recombination time:</strong> {processingTime?.recombinationTime}s
                    </div>
                    <div className="w-100 text-center mt-3">
                        {retrievedFileDetails && (retrievedFileDetails?.extraData?.filetype?.startsWith("image/") || retrievedFile?.startsWith("data:image")) ? (
                            <div className="w-100 d-flex flex-column">
                                <img src={retrievedFile} className="img-fluid" alt="Backup" fluid />
                                <Button
                                    variant="info"
                                    className="mt-3"
                                    onClick={openImageInNewTab}
                                >
                                    View Full Image ({getFormattedFileSize(retrievedFile)})
                                </Button>
                            </div>
                        ) : (
                            <>
                                File: {retrievedFileDetails?.extraData?.filename || retrievedFileDetails?.ExtraData?.filename || "untitled"}<br />
                                <a
                                    href={retrievedFile}
                                    download={retrievedFileDetails?.extraData?.filename || retrievedFileDetails?.ExtraData?.filename || "untitled"}
                                >
                                    <Button variant="success" className="mt-3">
                                        <i className={`bi ${getFileIcon(retrievedFileDetails?.extraData || retrievedFileDetails?.ExtraData)} me-2`}></i>
                                        Download File ({getFormattedFileSize(retrievedFile)})
                                    </Button>
                                </a>
                            </>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0">
                    <Button variant="secondary" onClick={() => setRetrievedFile(null)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={deleteModal}
                onHide={() => setDeleteModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deletion</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <h5>Are you sure you want to delete this image and all of its chunks?</h5>
                    <img className="img-fluid p-3" src={imageToDelete?.extraData?.ImageURL}></img>
                    <div className="text-truncate">
                        <strong>{imageToDelete?.extraData?.ImageURL}</strong>
                    </div>
                    <p>Note: This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                        {deleting ? <span className="d-flex flex-row align-items-center"><Spinner size='sm' className='me-2' />deleting...</span> : `Confirm Delete`}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Upload uploadModal={uploadModal} setUploadModal={setUploadModal} setRefresh={setRefresh} />
        </>
    );

};

const formatBytes = (bytes) => {
    if (isNaN(bytes) || bytes === 0) return "0 B";

    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const size = bytes / Math.pow(1024, i);
    const decimals = i === 0 ? 0 : i === 1 ? 1 : 2;

    return `${size.toFixed(decimals)} ${units[i]}`;
};

const getFormattedFileSize = (dataUri) => {
    if (!dataUri?.startsWith("data:")) return "Unknown";

    try {
        const base64 = dataUri.split(",")[1];
        const byteLength = (base64.length * 3) / 4 - (base64.endsWith("==") ? 2 : base64.endsWith("=") ? 1 : 0);
        return formatBytes(byteLength);
    } catch (err) {
        console.error("Error calculating file size:", err);
        return "Unknown";
    }
};

const getFileIcon = (extraData) => {
    // Try to get the extension from extraData
    const extension = extraData?.extension || extraData?.filename?.split('.').pop() || extraData?.ImageURL?.split('.').pop() || 'unknown';

    // Simple icon map for common extensions
    const iconMap = {
        'docx': 'file-word-fill',  // Word document
        'pdf': 'file-pdf-fill',  // PDF
        'webp': 'file-earmark-image',
        'jpg': 'file-earmark-image',  // Image (JPG)
        'jpeg': 'file-earmark-image',  // Image (JPEG)
        'png': 'file-earmark-image',  // Image (PNG)
        'zip': 'file-earmark-zip-fill',  // ZIP archive
        'txt': 'file-earmark-text-fill',  // Text file
        'json': 'file-earmark-code-fill',  // JSON file
        'mp3': 'file-earmark-music-fill',  // Music file
        'mp4': 'file-earmark-play-fill',  // Video file
    };

    // If the extension is recognized, return the mapped icon
    if (iconMap[extension]) {
        return `bi bi-${iconMap[extension]}`;
    }

    // If it's an unrecognized extension, fall back to 'filetype-{extension}' icon
    if (extension && extension !== 'unknown') {
        return `bi bi-filetype-${extension}`;
    }

    // If no valid extension, use a generic 'file-earmark' icon
    return 'bi bi-file-earmark-fill';
};